import React, { createContext, useContext, ReactNode } from 'react';
import { observer } from 'mobx-react';
import userStore from './user.store';
import orgsStore from './orgs.store';
import membersStore from './members.store';
import { canAddUserToCommunity } from '../utils/roles';
import { User } from '../utils/models';
import { DUMMY_USER, DUMMY_COMMUNITY_ID, DUMMY_COMMUNITY, DUMMY_MEMBERS } from '../pages/Khilanet/khilanetData';
import { getAuth as getKhilanetAuth } from '../pages/Khilanet/auth/khilanetAuth';

export interface CouponsAppData {
  // User
  userId: number | null;
  givenName: string;
  familyName: string;
  displayName: string;
  nickName: string;
  phones: string[];
  jwtToken: string;
  // Community
  currentCommunityId: number | null;
  currentCommunityName: string;
  communities: Array<{ id: number; name: string }>;
  // Members
  orgMembers: User[];
  orgMembersSorted: User[];
  // Permissions
  canAddToCommunity: boolean;
  // Ads
}

const CouponsAppDataContext = createContext<CouponsAppData | undefined>(undefined);

export function useCouponsAppData(): CouponsAppData {
  const context = useContext(CouponsAppDataContext);
  if (!context) {
    throw new Error('useCouponsAppData must be used within a CouponsAppDataProvider');
  }
  return context;
}

// Mekomi provider: bridges MobX stores into context
export const MekomiCouponsAppDataProvider: React.FC<{ children: ReactNode }> = observer(({ children }) => {
  const appData: CouponsAppData = {
    userId: userStore.user?.user_id ?? null,
    givenName: userStore.user?.given_name ?? '',
    familyName: userStore.user?.family_name ?? '',
    displayName: userStore.user?.displayName ?? '',
    nickName: userStore.user?.nick_name ?? '',
    phones: userStore.user?.phones ?? [],
    jwtToken: userStore.jwtToken ?? '',
    currentCommunityId: orgsStore.currentOrgId ?? null,
    currentCommunityName: orgsStore.currentOrg?.name ?? '',
    communities: orgsStore.data?.slice() ?? [],
    orgMembers: membersStore.orgMembers ?? [],
    orgMembersSorted: membersStore.orgMembersSorted ?? [],
    canAddToCommunity: canAddUserToCommunity(),
  };

  return (
    <CouponsAppDataContext.Provider value={appData}>
      {children}
    </CouponsAppDataContext.Provider>
  );
});

// Khilanet provider. Reads the signed-in session (set by the OTP screen via signIn()):
//   - has a cached profile  -> build appData from it (this is the normal path, real or dummy)
//   - signed in but profile fetch failed -> degraded fallback using the DUMMY_* placeholders
//     so the panel still renders rather than crashing on missing fields
// Read once at mount — KhilanetPage only renders this after isAuthed(), and a re-auth is a
// full navigation, so there's nothing to subscribe to here. Kehilanet has no "members of my
// community" endpoint, so orgMembers is empty for any real session.
export const KhilanetCouponsAppDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const auth = getKhilanetAuth();
  const u = auth?.user;

  const appData: CouponsAppData = u
    ? {
        userId: u.userId,
        givenName: u.givenName,
        familyName: u.familyName,
        displayName: u.displayName || `${u.givenName} ${u.familyName}`.trim(),
        nickName: u.nickName,
        phones: u.phones,
        jwtToken: auth?.accessToken ?? '',
        currentCommunityId: u.communities[0]?.id ?? null,
        currentCommunityName: u.communities[0]?.name ?? '',
        communities: u.communities,
        orgMembers: [],
        orgMembersSorted: [],
        canAddToCommunity: false,
      }
    : {
        userId: DUMMY_USER.user_id,
        givenName: DUMMY_USER.given_name,
        familyName: DUMMY_USER.family_name,
        displayName: DUMMY_USER.displayName,
        nickName: DUMMY_USER.nick_name,
        phones: DUMMY_USER.phones,
        jwtToken: auth?.accessToken ?? '',
        currentCommunityId: DUMMY_COMMUNITY_ID,
        currentCommunityName: DUMMY_COMMUNITY.name,
        communities: [DUMMY_COMMUNITY],
        orgMembers: DUMMY_MEMBERS,
        orgMembersSorted: DUMMY_MEMBERS,
        canAddToCommunity: false,
      };

  return (
    <CouponsAppDataContext.Provider value={appData}>
      {children}
    </CouponsAppDataContext.Provider>
  );
};

export default CouponsAppDataContext;
